import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Lock, LogIn, Key } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { API_CONFIG } from '@/config/api';
import { isDevelopment, bypassEnabled } from '@/config/auth';

const Login = () => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showBypass, setShowBypass] = useState(false);
  const { login } = useAuth();

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      await login(); // No password for Microsoft login
    } catch (err) {
      setError('Microsoft login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBypassLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Passwort ist erforderlich');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Test the password by making a simple API call
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${password}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await login(password); // Pass password for bypass auth
      } else {
        setError('Ungültiges Passwort');
      }
    } catch (error) {
      setError('Verbindung zum System fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <img
              src="/lr-logo-new.png"
              alt="L&R People.Health.Care. Logo"
              className="h-12 w-auto"
            />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold">L&R Chatbot Assistent</h1>
            <p className="text-muted-foreground">Geben Sie Ihr Zugangskennwort ein, um fortzufahren</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Microsoft Login Button - Primary Option */}
          <Button
            className="w-full"
            size="lg"
            onClick={handleMicrosoftLogin}
            disabled={isLoading}
          >
            <LogIn className="mr-2 h-4 w-4" />
            {isLoading ? 'Anmeldung...' : 'Mit Microsoft anmelden'}
          </Button>

          {/* Bearer Authentication Option - Only shown when bypass is enabled */}
          {bypassEnabled && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Alternative
                  </span>
                </div>
              </div>

              {!showBypass ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowBypass(true)}
                >
                  <Key className="mr-2 h-4 w-4" />
                  Passwort-Authentifizierung verwenden
                </Button>
              ) : (
                <form onSubmit={handleBypassLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">API Passwort</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Passwort eingeben..."
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading || !password}>
                    {isLoading ? 'Überprüfung...' : 'Zugriff auf System'}
                  </Button>
                </form>
              )}
            </>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="text-center space-y-2">
          <p className="text-xs text-muted-foreground">
            Ihre Sitzung läuft ab, wenn Sie diesen Browser-Tab schließen
          </p>
          {isDevelopment && bypassEnabled && (
            <p className="text-xs text-muted-foreground">
              Entwicklungsmodus - Beide Authentifizierungsmethoden verfügbar
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;